import { useCallback, useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiRefreshCw, FiStar } from "react-icons/fi";
import { getAdminCampaigns, setAdminCampaignFeatured } from "../api/admin";
import { useTranslation } from "../context/TranslationContext";
import { adminTheme } from "../styles/adminTheme";
import { formatCampaignMoney } from "../utils/campaignCardUtils";

/**
 * Which campaigns lead Discover's Featured row. Picks come first, newest pick first;
 * a pick that Discover wouldn't show anyway (paused, ended, members-only) says why.
 *
 * @see mintly-back/src/controllers/adminCampaignsController
 */

const SHOWN_STATUSES = ["active", "pending"];

const STATUS_KEYS = {
  active: "common.statusActive",
  pending: "common.statusPending",
  paused: "common.statusPaused",
  completed: "common.statusCompleted",
  cancelled: "common.statusCancelled",
};

const AdminCampaignsTab = ({ isDark, setToast, baseText, mutedText, cardBase }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [featuredCount, setFeaturedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  /** The row being saved, so its button can't be pressed twice. */
  const [savingId, setSavingId] = useState(null);

  const theme = adminTheme(isDark);
  const { inputBase, rowBorder } = theme;

  // Search after a pause in typing.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setQuery(search.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminCampaigns({
        search: query || undefined,
        featured: featuredOnly ? "true" : undefined,
        page,
        limit: 25,
      });
      setRows(res.data || []);
      setPages(res.pages || 1);
      setFeaturedCount(res.featuredCount || 0);
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("admin.featuredLoadError"));
    } finally {
      setLoading(false);
    }
  }, [query, featuredOnly, page, setToast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (row) => {
    setSavingId(row.id);
    try {
      await setAdminCampaignFeatured(row.id, !row.featured);
      setToast?.(row.featured ? t("admin.featuredRemoved") : t("admin.featuredAdded"));
      await load();
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("admin.featuredSaveError"));
    } finally {
      setSavingId(null);
    }
  };

  const hiddenReason = (row) => {
    if (!SHOWN_STATUSES.includes(row.status)) {
      return t("admin.featuredReasonStatus", { status: t(STATUS_KEYS[row.status] || "common.statusPending") });
    }
    if (row.visibility === "members_only") return t("admin.featuredReasonMembersOnly");
    return "";
  };

  return (
    <div className={`${cardBase} rounded-2xl p-5 sm:p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h3 className={`text-base font-semibold tracking-tight ${baseText}`}>
            {t("admin.featuredCampaigns")} ({t("admin.featuredCount", { count: featuredCount })})
          </h3>
          <p className={`text-xs ${mutedText} mt-1 max-w-3xl`}>{t("admin.featuredIntro")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("admin.featuredSearch")}
            className={`w-56 rounded-xl border px-3 py-2 text-sm outline-none ${inputBase}`}
          />
          <label className={`flex items-center gap-2 text-sm ${baseText}`}>
            <input
              type="checkbox"
              checked={featuredOnly}
              onChange={(e) => {
                setPage(1);
                setFeaturedOnly(e.target.checked);
              }}
            />
            {t("admin.featuredOnly")}
          </label>
          <button type="button" onClick={load} className={theme.btnSecondary} aria-label={t("admin.blockedRefresh")}>
            <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className={theme.tableWrap}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left ${theme.thead}`}>
              {[
                t("admin.featuredColCampaign"),
                t("admin.featuredColSpace"),
                t("admin.featuredColStatus"),
                t("admin.featuredColBudget"),
                t("admin.featuredColFeatured"),
              ].map((h) => (
                <th key={h} className={theme.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const reason = row.featured ? hiddenReason(row) : "";
              return (
                <tr key={row.id} className={`border-t ${rowBorder} ${theme.rowHover} align-top`}>
                  <td className="px-4 py-3 min-w-[200px]">
                    <div className={`font-semibold ${baseText}`}>{row.name || "—"}</div>
                    <div className={`text-[11px] ${mutedText}`}>
                      {row.owner?.businessName || row.owner?.username || ""}
                    </div>
                  </td>
                  <td className={`px-4 py-3 min-w-[140px] ${row.mintSpace ? baseText : mutedText}`}>
                    {row.mintSpace?.name || "—"}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${baseText}`}>
                    {t(STATUS_KEYS[row.status] || "common.statusPending")}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${baseText}`}>
                    {formatCampaignMoney(row.spent)} ₾ / {formatCampaignMoney(row.budget)} ₾
                  </td>
                  <td className="px-4 py-3 min-w-[170px]">
                    <button
                      type="button"
                      onClick={() => toggle(row)}
                      disabled={savingId === row.id}
                      className={row.featured ? theme.btnPrimary : theme.btnSecondary}
                    >
                      <FiStar className={`h-4 w-4 ${row.featured ? "fill-current" : ""}`} />
                      {row.featured ? t("admin.featuredRemove") : t("admin.featuredOff")}
                    </button>
                    {reason && (
                      <div className="mt-1 text-[11px] text-amber-400">
                        {t("admin.featuredHiddenNote", { reason })}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className={`py-10 text-center ${mutedText}`}>
                  {t("admin.featuredNone")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className={`text-xs ${mutedText}`}>{t("admin.featuredPage", { page, pages })}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className={`${theme.iconBtn} disabled:opacity-40`}
            aria-label={t("common.previous")}
          >
            <FiChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className={`${theme.iconBtn} disabled:opacity-40`}
            aria-label={t("common.next")}
          >
            <FiChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCampaignsTab;
